<template>
  <FormCard
    :title="$t('addressDocs.title')"
    :subtitle="$t('addressDocs.subtitle')"
    icon
    iconBgClass="bg-gradient-to-br from-yellow-500 to-orange-600"
  >
    <template #icon>
      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-4 sm:space-y-5" novalidate>
      <!-- Residence Address -->
      <div class="form-group">
        <label for="residence_address" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
            </div>
            <span class="text-sm sm:text-base">{{ $t('addressDocs.addressLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <input
          type="text"
          id="residence_address"
          :value="form.residence_address"
          @input="updateField('residence_address', $event.target.value)"
          @blur="validateField('residence_address')"
          :placeholder="$t('addressDocs.addressPlaceholder')"
          class="form-input"
          :class="{ 'error': localErrors.residence_address || errors.residence_address }"
          maxlength="200"
          autocomplete="street-address"
          required
        />
        <div class="text-right text-xs text-gray-500 mt-1">
          {{ form.residence_address ? form.residence_address.length : 0 }}/200 {{ $t('addressDocs.characters') }}
        </div>
        <div v-if="localErrors.residence_address || errors.residence_address" class="form-error" role="alert">
          {{ localErrors.residence_address || errors.residence_address }}
        </div>
      </div>

      <!-- Town/City -->
      <div class="form-group">
        <label for="town" class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span class="text-sm sm:text-base">{{ $t('addressDocs.townLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        <input
          type="text"
          id="town"
          :value="form.residence_town"
          @input="updateField('residence_town', $event.target.value)"
          @blur="validateField('residence_town')"
          :placeholder="$t('addressDocs.townPlaceholder')"
          class="form-input"
          :class="{ 'error': localErrors.residence_town || errors.residence_town }"
          maxlength="100"
          autocomplete="address-level2"
          required
        />
        <div v-if="localErrors.residence_town || errors.residence_town" class="form-error" role="alert">
          {{ localErrors.residence_town || errors.residence_town }}
        </div>
      </div>

      <!-- Document Type Selection -->
      <div class="form-group">
        <label class="form-label">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 sm:w-8 sm:h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span class="text-sm sm:text-base">{{ $t('addressDocs.uploadDocLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label 
            class="document-type-card" 
            :class="{ 'active': localDocType === 'id_card' }"
          >
            <input type="radio" value="id_card" v-model="localDocType" @change="onDocTypeChange" class="sr-only" />
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 block text-sm sm:text-base">{{ $t('addressDocs.idCardTitle') }}</span>
                <span class="text-xs text-gray-600">{{ $t('addressDocs.idCardHint') }}</span>
              </div>
            </div>
            <div v-if="localDocType === 'id_card'" class="check-icon flex-shrink-0">
              <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </label>

          <label 
            class="document-type-card" 
            :class="{ 'active': localDocType === 'passport' }"
          >
            <input type="radio" value="passport" v-model="localDocType" @change="onDocTypeChange" class="sr-only" />
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 block text-sm sm:text-base">{{ $t('addressDocs.passportTitle') }}</span>
                <span class="text-xs text-gray-600">{{ $t('addressDocs.passportHint') }}</span>
              </div>
            </div>
            <div v-if="localDocType === 'passport'" class="check-icon flex-shrink-0">
              <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </label>
        </div>
      </div>

      <!-- Restored photos banner -->
      <div v-if="hasRestoredPhoto && (localIdPhoto1Url || localPassportUrl)" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
        <div class="flex items-center gap-2 text-green-700">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span class="text-sm font-medium">{{ $t('addressDocs.restoredBanner') }}</span>
        </div>
        <p class="text-xs text-green-600 mt-1 ml-7 text-left">{{ $t('addressDocs.restoredBannerHint') }}</p>
      </div>

      <!-- ID Card Upload (Front & Back) -->
      <div v-if="localDocType === 'id_card'" class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <!-- ID Front Upload -->
        <div class="upload-card" :class="{ 'border-red-300': localErrors.id_photo1 }">
          <div class="text-center mb-2 sm:mb-3">
            <div class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-2">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 class="font-semibold text-gray-900 text-sm">{{ $t('addressDocs.idFrontLabel') }} <span class="text-red-500">*</span></h3>
            <p class="text-xs text-gray-500">{{ $t('addressDocs.idFrontHint') }}</p>
          </div>
          
          <label for="idPhoto1Input" class="upload-preview cursor-pointer block relative">
            <div v-if="uploadingFront" class="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl z-10">
              <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
            <img v-if="localIdPhoto1Url" :src="localIdPhoto1Url" alt="ID Front" class="preview-image" />
            <div v-else class="upload-placeholder">
              <svg class="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="text-xs text-gray-500">{{ $t('addressDocs.tapToUpload') }}</span>
            </div>
            <button 
              v-if="localIdPhoto1Url && !uploadingFront" 
              type="button"
              @click.prevent.stop="removeFile('front')"
              class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-20"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </label>
          
          <input 
            id="idPhoto1Input"
            type="file" 
            accept="image/jpeg,image/jpg,image/png,image/gif,image/heic,image/heif,image/*" 
            class="hidden" 
            @change="handleFrontUpload"
          />
          <div v-if="localErrors.id_photo1" class="form-error text-center text-xs mt-2">{{ localErrors.id_photo1 }}</div>
        </div>

        <!-- ID Back Upload -->
        <div class="upload-card" :class="{ 'border-red-300': localErrors.id_photo2 }">
          <div class="text-center mb-2 sm:mb-3">
            <div class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-2">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 class="font-semibold text-gray-900 text-sm">{{ $t('addressDocs.idBackLabel') }} <span class="text-red-500">*</span></h3>
            <p class="text-xs text-gray-500">{{ $t('addressDocs.idBackHint') }}</p>
          </div>
          
          <label for="idPhoto2Input" class="upload-preview cursor-pointer block relative">
            <div v-if="uploadingBack" class="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl z-10">
              <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
            <img v-if="localIdPhoto2Url" :src="localIdPhoto2Url" alt="ID Back" class="preview-image" />
            <div v-else class="upload-placeholder">
              <svg class="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="text-xs text-gray-500">{{ $t('addressDocs.tapToUpload') }}</span>
            </div>
            <button 
              v-if="localIdPhoto2Url && !uploadingBack" 
              type="button"
              @click.prevent.stop="removeFile('back')"
              class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-20"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </label>
          
          <input 
            id="idPhoto2Input"
            type="file" 
            accept="image/jpeg,image/jpg,image/png,image/gif,image/heic,image/heif,image/*" 
            class="hidden" 
            @change="handleBackUpload"
          />
          <div v-if="localErrors.id_photo2" class="form-error text-center text-xs mt-2">{{ localErrors.id_photo2 }}</div>
        </div>
      </div>

      <!-- Passport Upload -->
      <div v-else-if="localDocType === 'passport'" class="upload-card" :class="{ 'border-red-300': localErrors.passport }">
        <div class="text-center mb-3">
          <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mx-auto mb-2">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="font-semibold text-gray-900">{{ $t('addressDocs.passportBioLabel') }} <span class="text-red-500">*</span></h3>
          <p class="text-xs text-gray-500">{{ $t('addressDocs.passportBioHint') }}</p>
        </div>
        
        <label for="passportFileInput" class="upload-preview cursor-pointer block relative">
          <div v-if="uploadingPassport" class="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl z-10">
            <svg class="animate-spin h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
          <img v-if="localPassportUrl" :src="localPassportUrl" alt="Passport" class="preview-image" />
          <div v-else-if="passportFile && !localPassportUrl" class="flex flex-col items-center justify-center py-4">
            <svg class="w-12 h-12 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <span class="text-sm text-gray-700 font-medium">{{ passportFile.name }}</span>
            <span class="text-xs text-gray-500">{{ formatFileSize(passportFile.size) }}</span>
          </div>
          <div v-else class="upload-placeholder">
            <svg class="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span class="text-xs text-gray-500">{{ $t('addressDocs.tapToUploadPdf') }}</span>
          </div>
          <button 
            v-if="(localPassportUrl || passportFile) && !uploadingPassport" 
            type="button"
            @click.prevent.stop="removeFile('passport')"
            class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-20"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </label>
        
        <input 
          id="passportFileInput"
          type="file" 
          accept="image/jpeg,image/jpg,image/png,image/gif,image/heic,image/heif,image/*,application/pdf" 
          class="hidden" 
          @change="handlePassportUpload"
        />
        <div v-if="localErrors.passport" class="form-error text-center mt-2">{{ localErrors.passport }}</div>
      </div>

      <!-- General Error -->
      <div v-if="localErrors.general" class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center">
        <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        {{ localErrors.general }}
      </div>

      <!-- Navigation Buttons -->
      <div class="flex flex-col-reverse sm:flex-row justify-center gap-2 sm:gap-3 pt-4">
        <button 
          type="button" 
          @click="$emit('back')" 
          class="btn-secondary group"
          :disabled="isSubmitting"
        >
          <svg class="w-4 h-4 mr-1 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          {{ $t('addressDocs.backBtn') }}
        </button>
        
        <button 
          type="submit" 
          class="btn-primary group"
          :disabled="isSubmitting"
        >
          <svg v-if="isSubmitting" class="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>{{ isSubmitting ? $t('addressDocs.uploadingBtn') : $t('addressDocs.nextBtn') }}</span>
          <svg v-if="!isSubmitting" class="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </form>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';
import getAPI from '@/axios-api';
import JSZip from 'jszip';

export default {
  name: 'AddressDocuments',
  components: { FormCard },
  props: {
    form: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    documentType: { type: String, default: 'id_card' },
    idPhoto1Url: { type: String, default: null },
    idPhoto2Url: { type: String, default: null },
    passportPreviewUrl: { type: String, default: null },
    sessionUuid: { type: String, default: null },
    hasRestoredPhoto: { type: Boolean, default: false }
  },
  emits: ['submit', 'back', 'update:field', 'update:documentType', 'upload-front', 'upload-back', 'upload-passport'],
  data() {
    return {
      localDocType: this.documentType,
      localIdPhoto1Url: null,
      localIdPhoto2Url: null,
      localPassportUrl: null,
      frontFile: null,
      backFile: null,
      passportFile: null,
      uploadingFront: false,
      uploadingBack: false,
      uploadingPassport: false,
      isSubmitting: false,
      localErrors: {
        residence_address: '',
        residence_town: '',
        id_photo1: '',
        id_photo2: '',
        passport: '',
        general: ''
      }
    };
  },
  computed: {
    resolvedSessionUuid() {
      if (this.sessionUuid) return this.sessionUuid;
      if (this.form && this.form.session_uuid) return this.form.session_uuid;
      if (this.$parent && this.$parent.session_uuid) return this.$parent.session_uuid;
      const stored = localStorage.getItem('session_uuid');
      if (stored) return stored;
      return null;
    }
  },
  watch: {
    documentType: {
      immediate: true,
      handler(val) { this.localDocType = val; }
    },
    idPhoto1Url: {
      immediate: true,
      handler(val) { if (val) this.localIdPhoto1Url = val; }
    },
    idPhoto2Url: {
      immediate: true,
      handler(val) { if (val) this.localIdPhoto2Url = val; }
    },
    passportPreviewUrl: {
      immediate: true,
      handler(val) { if (val) this.localPassportUrl = val; }
    }
  },
  methods: {
    updateField(field, value) {
      this.$emit('update:field', { field, value });
      if (this.localErrors[field]) {
        this.localErrors[field] = '';
      }
    },
    
    onDocTypeChange() {
      this.$emit('update:documentType', this.localDocType);
      this.localErrors.id_photo1 = '';
      this.localErrors.id_photo2 = '';
      this.localErrors.passport = '';
    },
    
    validateField(field) {
      const value = this.form[field];
      switch (field) {
        case 'residence_address':
          if (!value || !value.trim()) {
            this.localErrors.residence_address = this.$t('addressDocs.errors.addressRequired');
            return false;
          }
          this.localErrors.residence_address = '';
          return true;
          
        case 'residence_town':
          if (!value || !value.trim()) {
            this.localErrors.residence_town = this.$t('addressDocs.errors.townRequired');
            return false;
          }
          this.localErrors.residence_town = '';
          return true;
      }
      return true;
    },
    
    validateFiles() {
      let isValid = true;
      if (this.hasRestoredPhoto) return true;
      
      if (this.localDocType === 'id_card') {
        if (!this.frontFile && !this.localIdPhoto1Url && !this.idPhoto1Url) {
          this.localErrors.id_photo1 = this.$t('addressDocs.errors.idFrontRequired');
          isValid = false;
        }
        if (!this.backFile && !this.localIdPhoto2Url && !this.idPhoto2Url) {
          this.localErrors.id_photo2 = this.$t('addressDocs.errors.idBackRequired');
          isValid = false;
        }
      } else if (this.localDocType === 'passport') {
        if (!this.passportFile && !this.localPassportUrl && !this.passportPreviewUrl) {
          this.localErrors.passport = this.$t('addressDocs.errors.passportRequired');
          isValid = false;
        }
      }
      return isValid;
    },
    
    validateAll() {
      let isValid = true;
      if (!this.validateField('residence_address')) isValid = false;
      if (!this.validateField('residence_town')) isValid = false;
      if (!this.validateFiles()) isValid = false;
      return isValid;
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    // Decode ANY image the browser can read (incl. HEIC on supporting
    // devices), downscale it and re-encode as JPEG under ~1.5 MB. This
    // removes the two causes of "Next does nothing": oversized phone photos
    // and HEIC files that failed the strict type check.
    async compressImage(file, maxBytes = 1.5 * 1024 * 1024, maxDim = 2000) {
      // PDFs pass through untouched (cannot be drawn to a canvas).
      if (file.type === 'application/pdf') return file;

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('read-failed'));
        reader.readAsDataURL(file);
      });

      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('decode-failed'));
        image.src = dataUrl;
      });

      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Step quality down until the JPEG is under the target size.
      let quality = 0.85;
      let blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
      while (blob && blob.size > maxBytes && quality > 0.4) {
        quality -= 0.1;
        blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
      }
      if (!blob) throw new Error('encode-failed');

      const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
      return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
    },

    async handleFrontUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadingFront = true;
      this.localErrors.id_photo1 = '';
      try {
        // Compress/convert to JPEG. Handles HEIC and large phone photos.
        const processed = await this.compressImage(file);
        this.frontFile = processed;
        if (this.localIdPhoto1Url) URL.revokeObjectURL(this.localIdPhoto1Url);
        this.localIdPhoto1Url = URL.createObjectURL(processed);
        this.$emit('upload-front', { processedFile: processed });
      } catch (error) {
        this.localErrors.id_photo1 = this.$t('addressDocs.errors.processFailed');
      } finally {
        this.uploadingFront = false;
      }
    },

    async handleBackUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadingBack = true;
      this.localErrors.id_photo2 = '';
      try {
        const processed = await this.compressImage(file);
        this.backFile = processed;
        if (this.localIdPhoto2Url) URL.revokeObjectURL(this.localIdPhoto2Url);
        this.localIdPhoto2Url = URL.createObjectURL(processed);
        this.$emit('upload-back', { processedFile: processed });
      } catch (error) {
        this.localErrors.id_photo2 = this.$t('addressDocs.errors.processFailed');
      } finally {
        this.uploadingBack = false;
      }
    },

    async handlePassportUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // PDFs keep a hard 10 MB ceiling (not an image, cannot compress).
      if (file.type === 'application/pdf' && file.size > 10 * 1024 * 1024) {
        this.localErrors.passport = this.$t('addressDocs.errors.fileTooLarge10');
        return;
      }

      this.uploadingPassport = true;
      this.localErrors.passport = '';
      try {
        // Images are compressed/converted; PDFs pass through unchanged.
        const processed = await this.compressImage(file);
        this.passportFile = processed;
        if (this.localPassportUrl) URL.revokeObjectURL(this.localPassportUrl);
        if (processed.type.startsWith('image/')) {
          this.localPassportUrl = URL.createObjectURL(processed);
        } else {
          this.localPassportUrl = null;
        }
        this.$emit('upload-passport', { processedFile: processed });
      } catch (error) {
        this.localErrors.passport = this.$t('addressDocs.errors.processFailed');
      } finally {
        this.uploadingPassport = false;
      }
    },
    
    removeFile(type) {
      if (type === 'front') {
        if (this.localIdPhoto1Url) URL.revokeObjectURL(this.localIdPhoto1Url);
        this.localIdPhoto1Url = null;
        this.frontFile = null;
        this.localErrors.id_photo1 = '';
      } else if (type === 'back') {
        if (this.localIdPhoto2Url) URL.revokeObjectURL(this.localIdPhoto2Url);
        this.localIdPhoto2Url = null;
        this.backFile = null;
        this.localErrors.id_photo2 = '';
      } else if (type === 'passport') {
        if (this.localPassportUrl) URL.revokeObjectURL(this.localPassportUrl);
        this.localPassportUrl = null;
        this.passportFile = null;
        this.localErrors.passport = '';
      }
    },
    
    async handleSubmit() {
      this.localErrors.general = '';
      // If any upload/field is still missing, tell the user WHY Next did
      // nothing instead of failing silently.
      if (!this.validateAll()) {
        this.localErrors.general = this.$t('addressDocs.errors.fixHighlighted');
        this.$nextTick(() => {
          const el = document.querySelector('.form-error, .border-red-300, .border-red-200');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      const sessionUuid = this.resolvedSessionUuid;
      if (!sessionUuid) {
        this.localErrors.general = this.$t('addressDocs.errors.sessionNotFound');
        return;
      }
      
      this.isSubmitting = true;
      try {
        const formData = new FormData();
        formData.append('session_uuid', sessionUuid);
        formData.append('step', '2');
        formData.append('residence_address', this.form.residence_address);
        formData.append('residence_town', this.form.residence_town);
        formData.append('document_type', this.localDocType);
        
        if (this.localDocType === 'id_card') {
          if (this.frontFile || this.backFile) {
            const zip = new JSZip();
            if (this.frontFile) zip.file(`front_${this.frontFile.name}`, this.frontFile);
            if (this.backFile) zip.file(`back_${this.backFile.name}`, this.backFile);
            const zipBlob = await zip.generateAsync({ 
              type: 'blob',
              compression: 'DEFLATE',
              compressionOptions: { level: 6 }
            });
            formData.append('id_photo', zipBlob, 'id_documents.zip');
          }
        } else if (this.localDocType === 'passport') {
          if (this.passportFile) {
            formData.append('passport_file', this.passportFile, this.passportFile.name);
          }
        }
        
        const response = await getAPI.post('/api/step/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 120000
        });
        
        this.$emit('submit', {
          success: true,
          nextStep: response.data.next_step || 3,
          data: response.data
        });
        
      } catch (error) {
        if (error.response?.data?.details) {
          const details = error.response.data.details;
          if (typeof details === 'object') {
            Object.entries(details).forEach(([field, msg]) => {
              const message = Array.isArray(msg) ? msg[0] : msg;
              if (field === 'id_photo' || field === 'id_photo1') {
                this.localErrors.id_photo1 = message;
              } else if (field === 'id_photo2') {
                this.localErrors.id_photo2 = message;
              } else if (field === 'passport_file' || field === 'passport') {
                this.localErrors.passport = message;
              } else if (Object.prototype.hasOwnProperty.call(this.localErrors, field)) {
                this.localErrors[field] = message;
              } else {
                this.localErrors.general = message;
              }
            });
          } else {
            this.localErrors.general = details;
          }
        } else if (error.response?.data?.error) {
          this.localErrors.general = error.response.data.error;
        } else {
          this.localErrors.general = error.message || this.$t('addressDocs.errors.uploadFailed');
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  
  beforeUnmount() {
    [this.localIdPhoto1Url, this.localIdPhoto2Url, this.localPassportUrl].forEach(url => {
      if (url && typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }
}
</script>

<style scoped>
.form-group {
  @apply w-full space-y-2;
}
.form-label {
  @apply block text-sm font-semibold text-gray-700;
}
.form-input {
  @apply w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white text-gray-900 rounded-xl border border-gray-300 
         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
         placeholder-gray-500 hover:border-gray-400 shadow-sm;
  font-size: 16px;
}
.form-input.error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}
.form-error {
  @apply text-red-600 text-sm flex items-center mt-1;
}
.form-error::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  flex-shrink: 0;
  background-image: url("data:image/svg+xml,%3Csvg fill='none' stroke='%23dc2626' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'%3E%3C/path%3E%3C/svg%3E");
}
.document-type-card {
  @apply bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-4 cursor-pointer
         transition-all duration-300 hover:border-blue-500 hover:shadow-lg
         flex items-center justify-between hover:scale-[1.01] relative;
  min-height: 70px;
}
.document-type-card.active {
  @apply border-blue-500 bg-blue-50/50 shadow-lg shadow-blue-500/20;
}
.check-icon {
  @apply w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full 
         flex items-center justify-center text-white;
}
.upload-card {
  @apply bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-4 transition-all duration-300 hover:border-blue-500;
}
.upload-preview {
  @apply border-2 border-dashed border-gray-300 rounded-xl p-3 sm:p-4
         transition-all duration-300 hover:border-blue-500 hover:bg-gray-50/50
         flex items-center justify-center;
  min-height: 120px;
  -webkit-tap-highlight-color: transparent;
}
@media (min-width: 640px) {
  .upload-preview {
    min-height: 150px;
  }
}
.preview-image {
  @apply w-full h-24 sm:h-32 object-contain rounded-lg;
}
.upload-placeholder {
  @apply flex flex-col items-center justify-center text-center;
}
.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}
.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 sm:py-3 px-4 sm:px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
  min-height: 44px;
}
</style>